'use client'

import { useCallback, useId, useRef } from 'react'
import type { KeyboardEvent } from 'react'

/**
 * The keyboard half of a `role="tablist"`, which is the half that gets left out.
 *
 * Declaring `role="tab"` and then leaving every button separately tabbable is
 * worse than declaring nothing: a screen reader announces a widget and then
 * promises its user a keyboard contract the page does not honour. The contract
 * is one tab stop for the whole group, the arrow keys along the row's own axis
 * to move inside it, Home/End to reach the ends, and a panel that says which
 * tab labels it.
 *
 * This exists because the repository had four `role="tablist"` widgets that are
 * the same widget — a row of buttons swapping the content below — and only one
 * of them implemented any of that. Writing a fifth partial copy beside them was
 * the alternative.
 *
 * Selection follows focus, which is the right choice here and not universally:
 * every panel these drive is already rendered client-side, so moving to a tab
 * costs nothing a keyboard user would notice. A tablist whose panels are
 * expensive to produce should use manual activation instead.
 *
 * Usage:
 *
 *   const tabs = useRovingTabs({ count: items.length, selectedIndex, onSelect })
 *   <div {...tabs.tablistProps} aria-label="…">
 *     {items.map((item, i) => <button {...tabs.tabProps(i)}>{item.label}</button>)}
 *   </div>
 *   <div {...tabs.panelProps}>…</div>
 *
 * `aria-label` on the tablist is deliberately not supplied here: only the call
 * site knows what the group is a list of.
 */
export function useRovingTabs({
  count,
  selectedIndex,
  onSelect,
  enabled = true,
  orientation = 'horizontal',
}: {
  count: number
  /** Index of the selected tab. Out-of-range values select nothing. */
  selectedIndex: number
  /**
   * Called with the newly selected index.
   *
   * `viaKeyboard` separates arrowing across the row from picking one: a caller
   * that writes the selection into history wants to replace on the first and
   * push on the second, or a keyboard user buries the page they arrived from
   * under a stack of their own tab changes.
   */
  onSelect: (index: number, context: { viaKeyboard: boolean }) => void
  /**
   * A group of one is not a tablist. When false every returned prop bag is
   * empty, so the call site keeps one spread instead of a conditional — and a
   * `tabpanel` can never be announced with no tabs to control it.
   */
  enabled?: boolean
  /**
   * Which axis the row occupies. A tablist owns the arrow keys along its own
   * axis and nothing else: claiming both axes means a keyboard user who presses
   * Down to scroll the page changes tab instead, and in a tablist whose panel
   * remounts on selection that silently discards whatever they had typed into
   * it. ARIA defaults a tablist to horizontal, so only the vertical case
   * announces itself.
   */
  orientation?: 'horizontal' | 'vertical'
}) {
  const idBase = useId()
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const panelId = `${idBase}-panel`
  const tabId = (index: number) => `${idBase}-tab-${index}`

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      // A held arrow key repeats roughly thirty times in two seconds, and each
      // repeat here is a navigation.
      if (event.repeat) return
      const last = count - 1
      if (last < 0) return
      const forwardKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'
      const backwardKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
      const next =
        event.key === forwardKey
          ? index === last
            ? 0
            : index + 1
          : event.key === backwardKey
            ? index === 0
              ? last
              : index - 1
            : event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? last
                : null
      if (next === null) return
      // Only the keys this tablist claims are swallowed; the cross-axis arrows
      // fall through so they still scroll the page.
      event.preventDefault()
      onSelect(next, { viaKeyboard: true })
      tabRefs.current[next]?.focus()
    },
    [count, onSelect, orientation],
  )

  if (!enabled) {
    return {
      tablistProps: {},
      tabProps: (_index: number) => ({}),
      panelProps: {},
    }
  }

  return {
    tablistProps: {
      role: 'tablist' as const,
      'aria-orientation':
        orientation === 'vertical' ? ('vertical' as const) : undefined,
    },

    tabProps: (index: number) => ({
      id: tabId(index),
      type: 'button' as const,
      role: 'tab' as const,
      'aria-selected': index === selectedIndex,
      'aria-controls': panelId,
      // One stop in the page's tab order for the whole group. When nothing is
      // selected the first tab holds it, so the group never becomes
      // unreachable by keyboard.
      tabIndex:
        index === selectedIndex || (selectedIndex < 0 && index === 0) ? 0 : -1,
      ref: (element: HTMLButtonElement | null) => {
        tabRefs.current[index] = element
      },
      onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) =>
        onKeyDown(event, index),
      onClick: () => onSelect(index, { viaKeyboard: false }),
    }),

    panelProps: {
      id: panelId,
      role: 'tabpanel' as const,
      'aria-labelledby':
        selectedIndex >= 0 && selectedIndex < count
          ? tabId(selectedIndex)
          : undefined,
    },
  }
}
